"use client";

import { useTranslations } from "next-intl";
import { RouteLoadingLinkStatus } from "@/features/navigation/ui/components/RouteLoadingSpinner";
import { getHomeCategoryListingPath } from "@/features/products/shared/categoryRoutes";
import { Link } from "@/i18n/navigation";
import { Image } from "@/lib/client/components/Image";
import { AspectRatio, cn, Grid, Section, Skeleton, VStack } from "@/ui";
import { getCategoryImageAlt } from "../../shared/heroLinks";
import type { Category } from "../../shared/types";
import { useCategories } from "../hooks/useCategories";

export type CategoriesSectionProps = {
  initialCategories?: Category[];
};

export const CategoriesSection = ({ initialCategories }: CategoriesSectionProps) => {
  const { data, isLoading } = useCategories(initialCategories);
  const navigationT = useTranslations("navigation");
  const generalT = useTranslations("general");

  if (isLoading) {
    return (
      <Section className="section-py">
        <Grid className="grid w-full grid-cols-2 gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton
              key={i}
              className="h-64 w-full rounded-sm"
            />
          ))}
        </Grid>
      </Section>
    );
  }

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <Section
      data-testid="products-section"
      className="section-spacing-xl"
    >
      {/* Section Header - asymmetric, editorial */}
      <div className="mb-16 md:mb-20 lg:mb-24">
        <div className="max-w-2xl">
          <span className="section-label block mb-4">{navigationT("categories_label")}</span>
          <h2 className="heading-display">{navigationT("categories_title")}</h2>
        </div>
      </div>

      {/* Asymmetric grid - creates visual interest */}
      <Grid className="grid w-full grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
        {data.map((category, index) => (
          <Link
            href={getHomeCategoryListingPath(category.label.label)}
            key={category.id}
            className={cn(
              "group relative block w-full overflow-hidden rounded-sm",
              // Create asymmetric sizing for visual interest
              index === 0 && "col-span-2 row-span-2 md:col-span-2 md:row-span-2",
              index === 1 && "col-span-1 md:col-span-1",
              index === 2 && "col-span-1 md:col-span-1",
              index > 2 && "col-span-1",
            )}
          >
            <RouteLoadingLinkStatus />
            <AspectRatio
              ratio={index === 0 ? 1 : 1}
              asChild
            >
              <div className="category-overlay h-full w-full">
                {/* Image with cinematic zoom */}
                <Image
                  src={category.image.src}
                  alt={getCategoryImageAlt(category.image.alt, navigationT(category.label.label))}
                  fill
                  sizes="(max-width: 768px) 50vw, 25vw"
                  quality={75}
                  priority={index === 0}
                  fetchPriority={index === 0 ? "high" : undefined}
                  style={{
                    objectFit: "cover",
                  }}
                  className="h-full w-full object-center transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                />

                {/* Content - minimal, elegant */}
                <VStack className="absolute inset-0 flex items-center justify-center p-4 sm:p-8">
                  {/* Refined glass effect */}
                  <div className="pointer-events-none absolute inset-x-6 sm:inset-x-8 top-1/2 h-[35%] -translate-y-1/2 bg-white/20 backdrop-blur-md transition-all duration-500 ease-out group-hover:bg-white/30" />

                  <VStack className="relative z-10 items-center gap-2">
                    <span
                      className="w-full text-center font-serif font-medium uppercase tracking-[0.15em] text-white drop-shadow-lg"
                      style={{
                        fontSize:
                          index === 0 ? "clamp(1.5rem, 5cqw, 3rem)" : "clamp(1rem, 3cqw, 1.5rem)",
                      }}
                    >
                      {navigationT(category.label.label)}
                    </span>
                    <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-white/90 transition-all duration-500 group-hover:text-white group-hover:tracking-[0.25em]">
                      {generalT("shop_now")}
                    </span>
                  </VStack>
                </VStack>

                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60 transition-opacity duration-500 group-hover:opacity-80" />
              </div>
            </AspectRatio>
          </Link>
        ))}
      </Grid>
    </Section>
  );
};
